import { ActionButton, Button } from '@react-spectrum/s2'
import { BookOpen, Download, ExternalLink, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { useEffect, useRef } from 'react'
import { trackAnalyticsEvent } from '@client/analytics'

interface Citation {
  title: string
  content: string
  pageStart?: number
  pageEnd?: number
  pageImageUrl?: string | null
  previewPageImageUrl?: string | null
  sourcePdfUrl?: string | null
  artifactAvailability?: {
    sourcePdf: boolean
    pageImage: boolean
  }
}

interface VisualEvidence {
  caption: string
  score?: number
  pageNumber: number
  pageImageUrl?: string | null
  previewPageImageUrl?: string | null
  sourcePdfUrl?: string | null
  artifactAvailability?: {
    sourcePdf: boolean
    pageImage: boolean
  }
  lowConfidence?: boolean
}

interface CitationPanelProps {
  citations: Array<Citation>
  onClose: () => void
  highlightLabel?: string | null
  filteredChunkIndices?: Array<number> | null
  visualEvidence?: Array<VisualEvidence>
  sourceReference?: {
    status: 'loading' | 'ready' | 'failed'
    referenceText?: string
    documentTitle?: string | null
    resolvedPageStart?: number | null
    resolvedPageEnd?: number | null
    pageImageUrl?: string | null
    sourcePdfUrl?: string | null
    exactText?: string | null
    resolvedHeading?: string | null
    failureReason?: string | null
  } | null
  onOpenSourcePdf?: (citation: Citation) => void
  onDownloadSourcePage?: (citation: Citation) => void
  showHeader?: boolean
}

function stripTrailingPageLabel(title: string) {
  return title.replace(/\s*\(page\s+\d+\)\s*$/i, '').trim()
}

export function CitationPanel({
  citations,
  onClose,
  highlightLabel,
  filteredChunkIndices,
  sourceReference,
  onOpenSourcePdf,
  onDownloadSourcePage,
  showHeader = true,
}: CitationPanelProps) {
  const itemRefs = useRef<Array<HTMLDivElement | null>>([])

  // Filter citations based on filteredChunkIndices OR highlightLabel
  const displayedCitations = filteredChunkIndices
    ? citations.filter((_, index) => filteredChunkIndices.includes(index))
    : highlightLabel
      ? citations.filter((c, index) => {
          const cleanLabel = highlightLabel
            .toLowerCase()
            .replace(/^source\s*/, '')
            .trim()
          const title = c.title.toLowerCase()
          const content = c.content.toLowerCase()

          // 1. Strict Index Mapping (Source 1.x -> Index x-1)
          const dotMatch = cleanLabel.match(/1\.(\d+)/)
          if (dotMatch) {
            const chunkIndex = parseInt(dotMatch[1], 10)
            if (!isNaN(chunkIndex)) {
              return index === chunkIndex - 1
            }
          }

          // 2. Fallback: Exact Source match (e.g. "Source 1")
          if (title.includes(`source ${cleanLabel}`)) return true

          // 3. Fallback: Content Match
          if (title.includes(cleanLabel) || content.includes(cleanLabel))
            return true

          return false
        })
      : citations

  // Fallback: If filtering returns nothing (e.g. bad label), show all
  const finalCitations =
    displayedCitations.length > 0 ? displayedCitations : citations

  useEffect(() => {
    // Scroll to top when filtered view changes
    if (highlightLabel && itemRefs.current[0]) {
      itemRefs.current[0]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [highlightLabel, finalCitations])

  return (
    <div
      className={`flex h-full min-h-0 flex-col bg-white dark:bg-neutral-950 ${
        showHeader ? 'border-l border-neutral-200 dark:border-neutral-800' : ''
      }`}
    >
      {showHeader ? (
        <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50/50 p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-bold">
              {sourceReference
                ? 'Selected source'
                : (highlightLabel || filteredChunkIndices) &&
                    finalCitations.length === 1
                  ? 'Selected source'
                  : 'Sources & citations'}
            </h2>
          </div>
          <ActionButton
            aria-label="Close citation panel"
            isQuiet
            size="S"
            onPress={() => {
              trackAnalyticsEvent('source_panel_closed', {
                has_source_reference: Boolean(sourceReference),
              })
              onClose()
            }}
          >
            <X className="h-5 w-5" />
          </ActionButton>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="space-y-6 p-4 pb-32">
          {sourceReference ? (
            sourceReference.status === 'loading' ? (
              <div className="rounded-xl border border-neutral-100 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
                <div className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {sourceReference.referenceText || 'Source reference'}
                </div>
                <div className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                  <span className="flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.3s]"></span>
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.15s]"></span>
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400"></span>
                  </span>
                  <span>Retrieving exact source reference for you...</span>
                </div>
              </div>
            ) : sourceReference.status === 'failed' ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                <div className="font-semibold">
                  {sourceReference.referenceText || 'Source reference'}
                </div>
                <p className="mt-2">
                  {sourceReference.failureReason ||
                    'The exact source reference could not be retrieved.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4 rounded-xl border border-neutral-100 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
                <div className="space-y-1.5">
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                    {sourceReference.documentTitle || 'Source document'}
                  </h3>
                  <div className="text-xs text-neutral-500">
                    {[
                      sourceReference.resolvedPageStart &&
                      sourceReference.resolvedPageEnd
                        ? sourceReference.resolvedPageStart ===
                          sourceReference.resolvedPageEnd
                          ? `Page ${sourceReference.resolvedPageStart}`
                          : `Pages ${sourceReference.resolvedPageStart}-${sourceReference.resolvedPageEnd}`
                        : null,
                      sourceReference.resolvedHeading || null,
                      sourceReference.referenceText || null,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 border-b border-neutral-100 pb-3 dark:border-neutral-800">
                  {sourceReference.sourcePdfUrl ? (
                    <>
                      <Button
                        size="S"
                        variant="secondary"
                        fillStyle="outline"
                        onPress={() => {
                          trackAnalyticsEvent('source_document_opened', {
                            source_context: 'source_reference',
                            has_page: Boolean(
                              sourceReference.resolvedPageStart,
                            ),
                          })
                          onOpenSourcePdf?.({
                            title:
                              sourceReference.documentTitle ||
                              'Source document',
                            content: '',
                            pageStart:
                              sourceReference.resolvedPageStart ?? undefined,
                            pageEnd:
                              sourceReference.resolvedPageEnd ?? undefined,
                            pageImageUrl: sourceReference.pageImageUrl ?? null,
                            sourcePdfUrl: sourceReference.sourcePdfUrl,
                          })
                        }}
                      >
                        Document
                        <ExternalLink size={14} />
                      </Button>
                      {sourceReference.resolvedPageStart ? (
                        <Button
                          size="S"
                          variant="secondary"
                          fillStyle="outline"
                          onPress={() => {
                            trackAnalyticsEvent('source_page_downloaded', {
                              source_context: 'source_reference',
                              has_page: true,
                            })
                            onDownloadSourcePage?.({
                              title:
                                sourceReference.documentTitle ||
                                'Source document',
                              content: '',
                              pageStart: sourceReference.resolvedPageStart,
                              pageEnd:
                                sourceReference.resolvedPageEnd ?? undefined,
                              pageImageUrl:
                                sourceReference.pageImageUrl ?? null,
                              sourcePdfUrl: sourceReference.sourcePdfUrl,
                            })
                          }}
                        >
                          Page
                          <Download size={14} />
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-800 dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-200">
                  {sourceReference.referenceText ? (
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
                      {sourceReference.referenceText}
                    </div>
                  ) : null}
                  <div>
                    <ReactMarkdown>
                      {sourceReference.exactText || ''}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            )
          ) : finalCitations.length === 0 ? (
            <div className="px-4 py-20 text-center">
              <BookOpen className="mx-auto mb-4 h-12 w-12 text-neutral-200 dark:text-neutral-800" />
              <p className="text-sm italic text-neutral-500">
                No citations found for this message.
              </p>
            </div>
          ) : (
            finalCitations.map((cite, i) => {
              const displayTitle = stripTrailingPageLabel(cite.title)
              const cleanLabel = highlightLabel
                ? highlightLabel
                    .toLowerCase()
                    .replace(/^source\s*/, '')
                    .trim()
                : ''

              // 1. Numeric Index Match (Source 1.x -> Index x-1)
              const dotMatch = cleanLabel.match(/1\.(\d+)/)
              let isHighlighted = false

              if (dotMatch) {
                const chunkIndex = parseInt(dotMatch[1], 10)
                // Important: we match against the original citations list index if possible,
                // but since we are mapping over finalCitations, we should compare indices carefully.
                // Actually, if we are in 1.x mode, we just check if this specific citation matches the index.
                // But wait, 'finalCitations' might be filtered.
                // The best way is to check if 'cite' matches the one at 'chunkIndex - 1' in the original 'citations'.
                isHighlighted =
                  !isNaN(chunkIndex) && cite === citations[chunkIndex - 1]
              } else {
                // Fallback to fuzzy matching
                isHighlighted = !!(
                  highlightLabel &&
                  (cite.title.toLowerCase().includes(cleanLabel) ||
                    cite.content.toLowerCase().includes(cleanLabel))
                )
              }

              const previewPageImageUrl = cite.previewPageImageUrl ?? null

              return (
                <div
                  key={i}
                  ref={(el) => {
                    itemRefs.current[i] = el
                  }}
                  className={`group space-y-3 rounded-xl border p-4 transition-all duration-500 ${
                    isHighlighted
                      ? 'scale-[1.02] border-blue-400 bg-blue-50/50 shadow-md ring-2 ring-blue-400/20 dark:bg-blue-900/20'
                      : 'border-neutral-100 bg-white hover:border-neutral-200 dark:border-neutral-800 dark:bg-neutral-900/50 dark:hover:border-neutral-700'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-4">
                      <h3
                        className={
                          isHighlighted
                            ? 'font-bold text-blue-700 dark:text-blue-400'
                            : 'font-semibold text-neutral-700 dark:text-neutral-300'
                        }
                      >
                        {displayTitle}
                      </h3>
                      <span
                        aria-hidden="true"
                        className="p-1 text-neutral-400 opacity-0 transition-all group-hover:opacity-100"
                      >
                        <ExternalLink size={14} />
                      </span>
                    </div>
                    {(cite.pageStart || cite.pageEnd) && (
                      <span className="block text-xs text-neutral-500">
                        {cite.pageStart &&
                        cite.pageEnd &&
                        cite.pageStart !== cite.pageEnd
                          ? `Pages ${cite.pageStart}-${cite.pageEnd}`
                          : `Page ${cite.pageStart || cite.pageEnd}`}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 border-b border-neutral-100 pb-3 dark:border-neutral-800">
                    {cite.sourcePdfUrl ? (
                      <>
                        <Button
                          size="S"
                          variant="secondary"
                          fillStyle="outline"
                          onPress={() => {
                            trackAnalyticsEvent('source_document_opened', {
                              source_context: 'citation',
                              has_page: Boolean(cite.pageStart || cite.pageEnd),
                            })
                            onOpenSourcePdf?.(cite)
                          }}
                        >
                          Document
                          <ExternalLink size={14} />
                        </Button>
                        {(cite.pageStart || cite.pageEnd) && (
                          <Button
                            size="S"
                            variant="secondary"
                            fillStyle="outline"
                            onPress={() => {
                              trackAnalyticsEvent('source_page_downloaded', {
                                source_context: 'citation',
                                has_page: true,
                              })
                              onDownloadSourcePage?.(cite)
                            }}
                          >
                            Page
                            <Download size={14} />
                          </Button>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-neutral-500">
                        Source PDF unavailable
                      </span>
                    )}
                  </div>
                  {previewPageImageUrl || cite.pageImageUrl ? (
                    <div className="space-y-2">
                      <img
                        src={previewPageImageUrl ?? cite.pageImageUrl!}
                        alt={`${displayTitle} page preview`}
                        className="w-full rounded-lg border border-neutral-200 bg-neutral-50 object-contain shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="prose max-w-none text-sm leading-relaxed prose-neutral dark:prose-invert">
                      <ReactMarkdown>{cite.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
